import React from 'react';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '../../../core/layouts/MainLayout';
import { ArrowUpRight } from 'lucide-react';
import { useJobs } from '../../../core/api/hooks/useJobs';
import { useSettings } from '../../../core/api/hooks/useSettings';

export const CareersPage = () => {
    const { t, i18n } = useTranslation();
    const { data: jobs, isLoading } = useJobs();
    const { data: settings } = useSettings();
    const isRTL = i18n.language === 'ar';

    return (
        <MainLayout forceSolidNavbar>
            <div className="bg-white min-h-screen pt-32 pb-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* Header */}
                    <div className="mb-24 grid lg:grid-cols-12 gap-8 items-end">
                        <div className="lg:col-span-8">
                            <h1 className="text-7xl sm:text-9xl font-display font-bold text-secondary mb-8 tracking-tighter opacity-10">
                                {t('careers.title', 'CAREERS')}
                            </h1>
                            <div className="-mt-12 sm:-mt-24 relative z-10 pl-2 lg:pl-12">
                                <h2 className="text-3xl sm:text-5xl font-bold text-secondary mb-6 max-w-2xl leading-tight">
                                    {t('careers.subtitle', 'Build your legacy with us.')}
                                </h2>
                                <p className="text-slate-500 max-w-lg">
                                    {t('careers.heroDescription')}
                                </p>
                            </div>
                        </div>
                        <div className="lg:col-span-4 flex justify-start lg:justify-end pb-2">
                            <a href="mailto:careers@elhesn.com" className="group inline-flex items-center gap-2 border-b border-secondary pb-1 text-secondary font-bold hover:text-primary hover:border-primary transition-colors">
                                {t('careers.emailUs', 'Send Unsolicited Application')}
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                        </div>
                    </div>

                    {/* Positions Grid (Table-like but responsive) */}
                    <div className="border-t border-slate-200">
                        {/* Table Header (Desktop) */}
                        <div className="hidden md:grid grid-cols-12 py-4 border-b border-slate-200 text-xs uppercase tracking-widest text-slate-400 font-bold">
                            <div className="col-span-1">#</div>
                            <div className="col-span-4">{t('careers.position', 'Position')}</div>
                            <div className="col-span-3">{t('careers.department', 'Department')}</div>
                            <div className="col-span-2">{t('careers.location', 'Location')}</div>
                            <div className="col-span-2 text-right">{t('careers.action', 'Action')}</div>
                        </div>

                        {/* Loading State */}
                        {isLoading && (
                            <div className="py-12 text-center text-slate-400 italic">
                                {t('careers.loading')}
                            </div>
                        )}

                        {/* Empty State */}
                        {!isLoading && jobs?.length === 0 && (
                            <div className="py-12 text-center text-slate-400 italic">
                                {t('careers.noJobs', 'No open positions at the moment.')}
                            </div>
                        )}

                        {/* Rows */}
                        {jobs?.map((job, index) => (
                            <div key={job.id} className="group grid grid-cols-1 md:grid-cols-12 py-8 md:py-6 gap-4 md:gap-0 border-b border-slate-200 items-center hover:bg-slate-50 transition-colors">
                                {/* Mobile Label is hidden, generic minimalist feel */}
                                <div className="col-span-1 text-slate-300 font-mono text-sm hidden md:block group-hover:text-primary transition-colors">
                                    {(index + 1).toString().padStart(2, '0')}
                                </div>
                                <div className="col-span-4">
                                    <h3 className="text-xl md:text-lg font-bold text-secondary group-hover:translate-x-2 transition-transform duration-300">
                                        {job.title}
                                    </h3>
                                    {/* Mobile only details */}
                                    <div className="md:hidden mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                                        <span className="bg-slate-100 px-2 py-1 rounded">{job.department}</span>
                                        <span className="bg-slate-100 px-2 py-1 rounded">{job.location}</span>
                                    </div>
                                </div>
                                <div className="hidden md:flex col-span-3 text-sm text-slate-500 items-center gap-2">
                                    {job.department}
                                </div>
                                <div className="hidden md:flex col-span-2 text-sm text-slate-500">
                                    {job.location}
                                </div>
                                <div className="col-span-2 flex justify-start md:justify-end mt-4 md:mt-0">
                                    {settings?.whatsapp ? (
                                        <a
                                            href={`https://wa.me/${settings.whatsapp.replace('+', '')}?text=${encodeURIComponent(t('careers.whatsappMessage', { jobTitle: job.title }))}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm font-bold uppercase tracking-wider text-secondary flex items-center gap-2 hover:text-primary transition-colors"
                                        >
                                            {t('careers.apply', 'Apply')} <span className={isRTL ? "w-8 h-px bg-secondary hover:bg-primary transition-colors block group-hover:w-12 rotate-180" : "w-8 h-px bg-secondary hover:bg-primary transition-colors block group-hover:w-12"}></span>
                                        </a>
                                    ) : (
                                        <a href="mailto:careers@elhesn.com" className="text-sm font-bold uppercase tracking-wider text-secondary flex items-center gap-2 hover:text-primary transition-colors">
                                            {t('careers.apply', 'Apply')} <span className={isRTL ? "w-8 h-px bg-secondary hover:bg-primary transition-colors block group-hover:w-12 rotate-180" : "w-8 h-px bg-secondary hover:bg-primary transition-colors block group-hover:w-12"}></span>
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-20 p-12 bg-surface border border-slate-200 relative overflow-hidden">
                        <div className="relative z-10 max-w-xl">
                            <h3 className="text-2xl font-display font-bold text-secondary mb-4">
                                {t('careers.noRole', "Don't see a role?")}
                            </h3>
                            <p className="text-slate-600 mb-8">
                                {t('careers.noRoleDescription')}
                            </p>
                            <a href="mailto:careers@elhesn.com" className="inline-block px-8 py-4 border border-secondary text-secondary font-bold hover:bg-secondary hover:text-white transition-all">
                                {t('careers.contactHr')}
                            </a>
                        </div>
                        {/* Abstract Decor */}
                        <div className="absolute top-0 right-0 w-64 h-full bg-slate-200/50 transform skew-x-12 translate-x-32 hidden sm:block"></div>
                    </div>

                </div>
            </div>
        </MainLayout>
    );
};
